/**
 * Support Case lifecycle (Owner decision): OPEN → CLOSED. Closed is FINAL.
 *
 * `in_progress` is an Open sub-state (Support Mode active). `resolved` is the
 * legacy persisted spelling of Closed — historical Cases are never rewritten,
 * so it is read/displayed exactly like `closed`.
 */
export const isOpenStatus = (status) =>
  status === "open" || status === "in_progress";

export const isClosedStatus = (status) =>
  status === "resolved" || status === "closed";

/** @deprecated legacy name — use isClosedStatus. */
export const isResolvedStatus = isClosedStatus;

/** Display label for optional Brand context on a Support Case. */
export const CLIENT_LEVEL_SUPPORT_CONTEXT = "Client / Brand Manager";

export const supportCaseContextLabel = (ticket) => {
  const name = String(ticket?.brandName || "").trim();
  if (name) return name;
  const id = String(ticket?.brandId || "").trim();
  if (id) return id;
  return CLIENT_LEVEL_SUPPORT_CONTEXT;
};

export const formatStatusLabel = (status) => {
  const raw = String(status || "").trim();
  if (!raw) return "Unknown";
  // Canonical user-facing lifecycle: Open / In Progress / Closed.
  if (isClosedStatus(raw)) return "Closed";
  return raw
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (isClosedStatus(s)) return "support-status-badge support-status-closed";
  switch (s) {
    case "open":
      return "support-status-badge support-status-open";
    case "in_progress":
      return "support-status-badge support-status-in-progress";
    default:
      return "support-status-badge support-status-unknown";
  }
};

/** Canonical machine-readable closure actor (server-projected). */
export const isClientClosed = (ticket) =>
  Boolean(ticket?.closedByClient) ||
  String(ticket?.closedByType || "").toLowerCase() === "client";

/**
 * Client-facing attribution — never expose Auth0 subs.
 *   staff  → "Alex Chen" / "KrossKast Support"
 *   client → "Teddy Boom (Client)" / "Client"   (Owner decision: Client close)
 * Rendered as "Closed by {label}".
 */
export const clientClosedByLabel = (ticket) => {
  const name = String(
    ticket?.closedByName || ticket?.resolvedByName || "",
  ).trim();
  if (isClientClosed(ticket)) return name ? `${name} (Client)` : "Client";
  if (name) return name;
  if (
    ticket?.closedAt ||
    ticket?.resolvedAt ||
    ticket?.closureNote ||
    ticket?.resolutionNote
  ) {
    return "KrossKast Support";
  }
  return null;
};

/** @deprecated legacy name — use clientClosedByLabel. */
export const clientResolvedByLabel = clientClosedByLabel;

/** When the Case was closed (public alias first, legacy attribute second). */
export const ticketClosedAt = (ticket) =>
  ticket?.closedAt || ticket?.resolvedAt || null;

/** Staff closure note (public alias first, legacy attribute second). */
export const ticketClosureNote = (ticket) =>
  ticket?.closureNote || ticket?.resolutionNote || null;

export const formatSupportWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
};

/** Closed-Case CSAT: 5-point faces scale (1 = very unsatisfied … 5 = very satisfied). */
export const CSAT_SCALE = Object.freeze([
  { value: 1, face: "😠", label: "Very unsatisfied" },
  { value: 2, face: "🙁", label: "Unsatisfied" },
  { value: 3, face: "😐", label: "Neutral" },
  { value: 4, face: "🙂", label: "Satisfied" },
  { value: 5, face: "😄", label: "Very satisfied" },
]);

export const CSAT_QUESTION =
  "How satisfied were you with the support you received?";
export const CSAT_FEEDBACK_PROMPT = "Anything else you'd like us to know?";

/** Closure guidance shown in place of the reply composer on a Closed Case. */
export const CLOSED_CASE_NOTICE = "This Support Case is closed.";
export const CLOSED_CASE_CTA = "Need more help? Open a new Support Case.";

/** Client-initiated close (Owner decision) — secondary action + confirmation copy. */
export const CLIENT_CLOSE_ACTION = "Close Support Case";
export const CLIENT_CLOSE_TITLE = "Close Support Case?";
export const CLIENT_CLOSE_BODY =
  "Are you sure you no longer need help with this Support Case? Once closed, this Case cannot be reopened. If you need help again, you can open a new Support Case.";
export const CLIENT_CLOSE_CANCEL = "Cancel";

/** New Support Case description minimum (server enforces the same rule). */
export const NEW_CASE_MIN_CHARS = 10;
export const NEW_CASE_MIN_CHARS_ERROR = `Please enter at least ${NEW_CASE_MIN_CHARS} characters to submit a Support Case.`;

/** Live refresh cadence for already-open Support views (lightweight polling). */
export const SUPPORT_LIST_POLL_MS = 15000;
export const SUPPORT_THREAD_POLL_MS = 15000;

const parseTs = (v) => {
  const n = Date.parse(String(v || ""));
  return Number.isFinite(n) ? n : 0;
};
const cmpDesc = (a, b) => (a === b ? 0 : a > b ? -1 : 1);

/**
 * Owner-canonical Support Case ordering per tab (mirrors the API's
 * `sortSupportTicketsForView` and SA's `sortSupportCasesForView`):
 *   all / open → most recently active (updatedAt, else createdAt) first
 *   closed     → most recently closed (closedAt / legacy resolvedAt) first
 * Deterministic tie-breaks: activity DESC, createdAt DESC, ticketId DESC.
 * "Case: 01" (clientCaseNumber) is display-only and never drives position.
 */
export function sortTicketsForTab(tickets, tab) {
  const activity = (t) => parseTs(t?.updatedAt) || parseTs(t?.createdAt);
  const closedAt = (t) =>
    parseTs(t?.closedAt) || parseTs(t?.resolvedAt) || activity(t);
  const primary = tab === "closed" ? closedAt : activity;
  return [...(Array.isArray(tickets) ? tickets : [])].sort(
    (a, b) =>
      cmpDesc(primary(a), primary(b)) ||
      cmpDesc(activity(a), activity(b)) ||
      cmpDesc(parseTs(a?.createdAt), parseTs(b?.createdAt)) ||
      cmpDesc(String(a?.ticketId || ""), String(b?.ticketId || "")),
  );
}
